/* =====================================================================
   Anagenesis — global scripts
   - Header mobile menu
   - Header search overlay with live (predictive) results
   - Hero parallax (hero + header scroll slower than the page)
   - AJAX add to cart, cart count and "added to cart" notification
   - Section initialisers that re-run in the theme editor
   ===================================================================== */

(function () {
  'use strict';

  /* -------------------------------------------------------------------
     Section initialisers
     Sections register with Anagenesis.register('name', fn). fn receives
     the section's root element and runs on page load and again whenever
     the theme editor re-renders that section.
     ------------------------------------------------------------------- */
  var registry = {};
  var Anagenesis = (window.Anagenesis = window.Anagenesis || {});

  Anagenesis.register = function (name, init) {
    registry[name] = init;
    document.querySelectorAll('[data-anag-section="' + name + '"]').forEach(function (el) {
      runInit(name, el);
    });
  };

  function runInit(name, el) {
    if (!registry[name] || el.__anagInit) return;
    el.__anagInit = true;
    try {
      registry[name](el);
    } catch (e) {
      console.error('[Anagenesis] ' + name, e);
    }
  }

  document.addEventListener('shopify:section:load', function (event) {
    event.target.querySelectorAll('[data-anag-section]').forEach(function (el) {
      runInit(el.getAttribute('data-anag-section'), el);
    });
    initHeader();
    initSearch();
    initParallax();
  });

  /* -------------------------------------------------------------------
     Header — mobile menu open / close
     ------------------------------------------------------------------- */
  function initHeader() {
    var header = document.getElementById('siteHeader');
    var toggle = document.getElementById('menuToggle');
    if (!header || !toggle || header.__anagInit) return;
    header.__anagInit = true;

    var setMenu = function (open) {
      header.classList.toggle('menu-open', open);
      toggle.setAttribute('aria-expanded', open);
    };
    toggle.addEventListener('click', function () {
      setMenu(!header.classList.contains('menu-open'));
    });

    // stagger index for the link animation
    header.querySelectorAll('.mobile-nav a').forEach(function (a, i) {
      a.style.setProperty('--i', i);
      a.addEventListener('click', function () {
        setMenu(false);
      });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') setMenu(false);
    });
    window.matchMedia('(min-width:1280px)').addEventListener('change', function (e) {
      if (e.matches) setMenu(false);
    });
  }

  /* -------------------------------------------------------------------
     Header search — full-screen overlay that slides down. Typing fetches
     Shopify predictive search rendered by sections/predictive-search.liquid
     and highlights the typed characters in the result titles.
     ------------------------------------------------------------------- */
  function initSearch() {
    var overlay = document.getElementById('SearchOverlay');
    if (!overlay || overlay.__anagInit) return;
    overlay.__anagInit = true;

    var input = overlay.querySelector('[data-search-input]');
    var results = overlay.querySelector('[data-search-results]');
    var status = overlay.querySelector('[data-search-status]');
    var triggers = document.querySelectorAll('[data-search-open]');
    var cache = {};
    var lastTrigger = null;
    var timer = null;
    var controller = null;
    var current = '';

    function isOpen() {
      return overlay.classList.contains('is-open');
    }

    function setOpen(open, trigger) {
      if (open === isOpen()) return;
      overlay.classList.toggle('is-open', open);
      overlay.setAttribute('aria-hidden', open ? 'false' : 'true');
      document.documentElement.classList.toggle('search-open', open);
      triggers.forEach(function (t) {
        t.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
      if (open) {
        lastTrigger = trigger || document.activeElement;
        // close the mobile menu if it was open
        var header = document.getElementById('siteHeader');
        var toggle = document.getElementById('menuToggle');
        if (header && toggle && header.classList.contains('menu-open')) toggle.click();
        requestAnimationFrame(function () {
          input.focus({ preventScroll: true });
          input.select();
        });
      } else if (lastTrigger && lastTrigger.focus) {
        lastTrigger.focus({ preventScroll: true });
      }
    }

    triggers.forEach(function (t) {
      t.addEventListener('click', function (e) {
        e.preventDefault();
        setOpen(true, t);
      });
    });
    overlay.querySelectorAll('[data-search-close]').forEach(function (b) {
      b.addEventListener('click', function () {
        setOpen(false);
      });
    });

    function escapeRegExp(str) {
      return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    function highlight(query) {
      var terms = query.split(/\s+/).filter(Boolean).map(escapeRegExp);
      if (!terms.length) return;
      var re = new RegExp('(' + terms.join('|') + ')', 'gi');
      results.querySelectorAll('[data-hl]').forEach(function (el) {
        var parts = el.textContent.split(re);
        el.textContent = '';
        parts.forEach(function (part, i) {
          if (!part) return;
          if (i % 2) {
            var mark = document.createElement('mark');
            mark.textContent = part;
            el.appendChild(mark);
          } else {
            el.appendChild(document.createTextNode(part));
          }
        });
      });
    }

    function render(query, html) {
      if (query !== current) return;
      results.innerHTML = html;
      highlight(query);
      status.textContent = '';
    }

    function search(query) {
      current = query;
      if (controller) controller.abort();
      if (!query) {
        results.innerHTML = '';
        status.textContent = '';
        return;
      }
      if (cache[query] !== undefined) {
        render(query, cache[query]);
        return;
      }
      status.textContent = status.getAttribute('data-loading-text') || '';
      controller = window.AbortController ? new AbortController() : null;
      var url =
        ((window.routes && window.routes.predictive_search_url) || '/search/suggest') +
        '?q=' + encodeURIComponent(query) +
        '&resources[type]=product,collection' +
        '&resources[limit]=10&resources[limit_scope]=each' +
        '&resources[options][prefix]=last' +
        '&section_id=predictive-search';
      fetch(url, { signal: controller ? controller.signal : undefined })
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.text();
        })
        .then(function (text) {
          var doc = new DOMParser().parseFromString(text, 'text/html');
          var section = doc.getElementById('shopify-section-predictive-search');
          var html = section ? section.innerHTML : '';
          cache[query] = html;
          render(query, html);
        })
        .catch(function (err) {
          if (err.name === 'AbortError') return;
          if (query === current) status.textContent = '';
          console.error('[Anagenesis] search', err);
        });
    }

    input.addEventListener('input', function () {
      clearTimeout(timer);
      var query = input.value.trim();
      timer = setTimeout(function () {
        search(query);
      }, 200);
    });

    // keyboard: Esc closes, arrows move through results, Tab stays inside
    overlay.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
        return;
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        var links = Array.prototype.slice.call(results.querySelectorAll('a'));
        if (!links.length) return;
        var i = links.indexOf(document.activeElement);
        e.preventDefault();
        if (e.key === 'ArrowDown') {
          (links[i + 1] || links[0]).focus();
        } else if (i <= 0) {
          input.focus();
        } else {
          links[i - 1].focus();
        }
        return;
      }
      if (e.key === 'Tab') {
        var focusables = Array.prototype.slice.call(
          overlay.querySelectorAll('button, input:not([type="hidden"]), a[href]')
        );
        var first = focusables[0];
        var last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });
  }

  /* -------------------------------------------------------------------
     Hero parallax — the hero section (and the header sitting over it)
     scroll at data-parallax-speed of the page speed, so the sections
     below scroll over them.
     ------------------------------------------------------------------- */
  var parallax = { layers: [], speed: 0.5, limit: 0, ticking: false, bound: false };
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function initParallax() {
    parallax.layers.forEach(function (el) {
      el.style.transform = '';
      el.classList.remove('parallax-layer');
    });
    parallax.layers = [];
    document.body.classList.remove('has-parallax');

    var hero = document.querySelector('[data-hero-parallax]');
    var enabled = window.themeSettings ? window.themeSettings.enableParallax : true;
    if (!hero || !enabled) return;

    var wrapper = hero.closest('.shopify-section') || hero;
    var header = document.querySelector('.shopify-section-group-header-group header.site-header');
    // only move the header along when it sits over this hero (hero is the first section)
    var first = document.querySelector('#MainContent > .shopify-section');
    parallax.layers = [wrapper];
    if (header && first === wrapper) parallax.layers.push(header);
    parallax.speed = parseFloat(hero.getAttribute('data-parallax-speed')) || 0.5;
    parallax.layers.forEach(function (el) {
      el.classList.add('parallax-layer');
    });
    document.body.classList.add('has-parallax');

    if (!parallax.bound) {
      parallax.bound = true;
      window.addEventListener('scroll', requestParallax, { passive: true });
      window.addEventListener('resize', requestParallax);
      reduceMotion.addEventListener('change', requestParallax);
    }
    updateParallax();
  }

  function requestParallax() {
    if (parallax.ticking) return;
    parallax.ticking = true;
    requestAnimationFrame(updateParallax);
  }

  function updateParallax() {
    parallax.ticking = false;
    if (!parallax.layers.length) return;
    var offset = 0;
    if (!reduceMotion.matches) {
      // stop once the hero is fully covered — no need to keep moving it
      var y = Math.min(window.scrollY, parallax.layers[0].offsetHeight * 2);
      offset = y * (1 - parallax.speed);
    }
    var value = offset ? 'translate3d(0, ' + offset + 'px, 0)' : '';
    parallax.layers.forEach(function (el) {
      el.style.transform = value;
    });
  }

  /* -------------------------------------------------------------------
     Cart
     ------------------------------------------------------------------- */
  Anagenesis.updateCartCount = function (count) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = count > 0 ? count : '';
      el.setAttribute('data-count', count);
    });
    var link = document.getElementById('HeaderCartLink');
    if (link && link.dataset.labelTemplate) {
      link.setAttribute('aria-label', link.dataset.labelTemplate.replace('{{ count }}', count));
    }
  };

  Anagenesis.refreshCartCount = function () {
    return fetch(window.routes.cart_url + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        Anagenesis.updateCartCount(cart.item_count);
        return cart;
      });
  };

  var notification = null;
  var notificationTimer = null;

  Anagenesis.showCartNotification = function (item) {
    notification = notification || document.getElementById('CartNotification');
    if (!notification) return;
    var img = notification.querySelector('[data-cart-notification-image]');
    var name = notification.querySelector('[data-cart-notification-name]');
    var variant = notification.querySelector('[data-cart-notification-variant]');
    if (img) {
      if (item.image) {
        img.src = item.image + (item.image.indexOf('?') > -1 ? '&' : '?') + 'width=160';
        img.alt = item.product_title || '';
        img.hidden = false;
      } else {
        img.hidden = true;
      }
    }
    if (name) name.textContent = item.product_title || '';
    if (variant) {
      var hasVariant = item.variant_title && !item.product_has_only_default_variant;
      variant.textContent = hasVariant ? item.variant_title : '';
    }
    notification.classList.add('is-active');
    clearTimeout(notificationTimer);
    notificationTimer = setTimeout(Anagenesis.hideCartNotification, 6000);
  };

  Anagenesis.hideCartNotification = function () {
    if (notification) notification.classList.remove('is-active');
  };

  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-cart-notification-close]')) Anagenesis.hideCartNotification();
  });

  /**
   * Adds the form's variant to the cart via the AJAX API.
   * Falls back to a normal form submit when "cart type" is set to "page".
   */
  Anagenesis.addToCart = function (form, button) {
    var cartType = window.themeSettings ? window.themeSettings.cartType : 'notification';
    if (cartType === 'page') {
      form.submit();
      return Promise.resolve();
    }
    var errorEl = form.querySelector('[data-form-error]');
    if (errorEl) {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }
    var label = button ? button.querySelector('[data-button-label]') || button : null;
    var original = label ? label.textContent : '';
    if (button) {
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('is-loading');
      if (label) label.textContent = window.themeStrings.adding;
    }

    return fetch(window.routes.cart_add_url + '.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: new FormData(form)
    })
      .then(function (response) {
        return response.text().then(function (text) {
          var data;
          try {
            data = JSON.parse(text);
          } catch (e) {
            throw { description: window.themeStrings.cartError };
          }
          if (!response.ok || data.status) throw data;
          return data;
        });
      })
      .then(function (item) {
        Anagenesis.showCartNotification(item);
        return Anagenesis.refreshCartCount();
      })
      .catch(function (err) {
        var message = (err && (err.description || err.message)) || window.themeStrings.cartError;
        if (errorEl) {
          errorEl.textContent = message;
          errorEl.hidden = false;
        } else {
          window.alert(message);
        }
      })
      .finally(function () {
        if (button) {
          button.removeAttribute('aria-disabled');
          button.classList.remove('is-loading');
          if (label) label.textContent = original;
        }
      });
  };

  // any product form marked data-ajax-cart submits through the AJAX API
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('form[data-ajax-cart]');
    if (!form) return;
    var cartType = window.themeSettings ? window.themeSettings.cartType : 'notification';
    if (cartType === 'page') return;
    e.preventDefault();
    var button = form.querySelector('[type="submit"]');
    if (button && button.getAttribute('aria-disabled') === 'true') return;
    Anagenesis.addToCart(form, button);
  });

  /* -------------------------------------------------------------------
     Accordions — any [data-accordion] with .productDetails-accordionItem
     children (product page, FAQ). One item open at a time.
     ------------------------------------------------------------------- */
  Anagenesis.initAccordion = function (root) {
    var items = root.querySelectorAll('.productDetails-accordionItem');
    items.forEach(function (item) {
      var header = item.querySelector('.productDetails-accordionHeader');
      var content = item.querySelector('.productDetails-accordionContent');
      if (!header || !content || header.__anagInit) return;
      header.__anagInit = true;
      header.addEventListener('click', function () {
        var isOpen = item.classList.contains('is-open');
        items.forEach(function (i) {
          i.classList.remove('is-open');
          var h = i.querySelector('.productDetails-accordionHeader');
          var c = i.querySelector('.productDetails-accordionContent');
          if (h) h.setAttribute('aria-expanded', 'false');
          if (c) c.style.maxHeight = null;
        });
        if (!isOpen) {
          item.classList.add('is-open');
          header.setAttribute('aria-expanded', 'true');
          content.style.maxHeight = content.scrollHeight + 'px';
        }
      });
    });
  };

  function initAccordions() {
    document.querySelectorAll('[data-accordion]').forEach(Anagenesis.initAccordion);
  }
  document.addEventListener('shopify:section:load', initAccordions);
  document.addEventListener('shopify:block:select', function (e) {
    var item = e.target.closest ? e.target.closest('.productDetails-accordionItem') : null;
    if (item && !item.classList.contains('is-open')) {
      var header = item.querySelector('.productDetails-accordionHeader');
      if (header) header.click();
    }
  });

  /* ------------------------------------------------------------------- */
  function onReady() {
    initHeader();
    initSearch();
    initParallax();
    initAccordions();
    document.querySelectorAll('[data-anag-section]').forEach(function (el) {
      runInit(el.getAttribute('data-anag-section'), el);
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }
})();

/* ---------- Anchor links to homepage sections (e.g. /#the-science) ----------
   From another page the browser loads the homepage and jumps to the id; images
   above can finish loading afterwards and shift it, so re-align once loaded.
   On the homepage itself, scroll smoothly instead of jumping. */
(function () {
  function target(hash) {
    if (!hash || hash.length < 2) return null;
    try { return document.getElementById(decodeURIComponent(hash.slice(1))); } catch (e) { return null; }
  }
  function realign() {
    var el = target(location.hash);
    if (el) el.scrollIntoView({ block: 'start' });
  }
  if (location.hash) {
    window.addEventListener('load', function () { setTimeout(realign, 50); });
  }
  document.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('a[href*="#"]');
    if (!a || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var url = new URL(a.href, location.href);
    if (url.origin !== location.origin || url.pathname !== location.pathname || !url.hash) return;
    var el = target(url.hash);
    if (!el) return;
    e.preventDefault();
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    history.pushState(null, '', url.hash);
  });
})();

